/**
 * Easely Scroll Module - Expert Production Grade
 * v1.0.4 - Precision ScrollTrigger Orchestration
 */
import { registry } from '../core/registry';

export const scrollEngine = {
  /**
   * Safely access the global ScrollTrigger plugin
   */
  getST() {
    return typeof window !== 'undefined' ? (window.ScrollTrigger || null) : null;
  },

  /**
   * Creates a high-performance ScrollTrigger for an element
   * @param {HTMLElement} el - The target element
   * @param {Object} settings - Parsed attributes from parser.js
   * @param {Function} onEnterCallback - Triggered when element enters viewport
   */
  createTrigger(el, settings, onEnterCallback) {
    const ST = this.getST();
    const gsap = typeof window !== 'undefined' ? window.gsap : null;

    if (!ST || !gsap || !el) {
      console.warn("Easely: GSAP ScrollTrigger not found. Skipping scroll animation.");
      return;
    }

    // 1. Prepare the Configuration Object
    const triggerConfig = {
      trigger: el,
      start: settings.scrollStart || "top 85%",
      
      // EXPERT FEATURE: Visual Debugging
      // Allows you to see the trigger lines using data-ez-markers="true"
      markers: settings.markers || false, 
      
      // 'once' ensures the animation doesn't reset when scrolling back up
      once: settings.once !== false,
      
      // EXPERT PERFORMANCE PROPERTIES
      fastScrollEnd: true,      // Kills the animation if user scrolls past instantly
      anticipatePin: 1,         // Smooths out start-of-animation measurement
      
      // Callback logic
      onEnter: () => {
        if (typeof onEnterCallback === 'function') {
          onEnterCallback();
        }
      }
    };

    // 2. Handle Scrubbing (The 'Liquid' parallax effect)
    // If settings.scrub is active, ST takes direct control of the tween
    if (settings.scrub) {
      triggerConfig.scrub = settings.scrub;
      // When scrubbing, we usually want to be able to reverse the motion
      triggerConfig.once = false; 
    }

    // 3. Initialize the Trigger
    try {
      const trigger = ST.create(triggerConfig);
      
      // 4. Register for memory management & cleanup
      registry.register(el, trigger);
      
      return trigger;
    } catch (error) {
      console.error("Easely ScrollEngine: Failed to create ScrollTrigger.", error);
      return null;
    }
  },

  /**
   * Forces a global recalculation of all scroll markers
   * Essential for dynamic height pages or image-heavy layouts
   */
  refresh() {
    const ST = this.getST();
    if (ST) {
      ST.refresh();
      console.log("Easely: ScrollTrigger markers refreshed.");
    }
  }
};